import {
  forwardRef,
  type KeyboardEvent,
  type MouseEvent,
  type PointerEvent,
  type ReactNode,
} from "react"

// Overlapping avatar cluster shared by the deal cards, the
// contacts "Owner" cell, and the phone-numbers "Assigned to"
// cell: up to `max` colored initial discs overlapped by 30%,
// each with a card-colored ring, plus a "+N" overflow tile.
//
// Two render modes:
// - Passive (no `onClick`): a plain <span>, used inside a
//   larger trigger (e.g. the contacts cell button).
// - Interactive (`onClick` set): a <span role="button"> with
//   keyboard activation. Deliberately NOT a <button> — the
//   deal card is itself a <button>, and nesting buttons is
//   invalid HTML. Click AND pointerdown stop propagation so
//   the parent card's activate handler and dnd-kit's drag
//   listeners don't swallow the interaction.

export interface AvatarStackPerson {
  id: number
  name: string
  initials: string
  color: string
}

// Initials must stay legible on every V2::AvatarColor
// background — the palette includes ink (#1E1B17), where
// dark text disappears. Same convention as ContactAvatar:
// cream on the ink ground, white everywhere else.
export function initialsColor(background: string): string {
  return background.toLowerCase() === "#1e1b17" ? "#F0EDE6" : "#FFFFFF"
}

interface AvatarStackProps {
  people: AvatarStackPerson[]
  // Disc diameter in px. 18 matches the kanban card design;
  // the table cells pass 22.
  size?: number
  // Max visible discs before the "+N" tile.
  max?: number
  // Optional leading element rendered before the first
  // avatar (the phone-numbers AI dot). When present, the
  // first avatar overlaps it like any other stack member.
  leading?: ReactNode
  onClick?: () => void
  ariaLabel?: string
  className?: string
}

export const AvatarStack = forwardRef<HTMLSpanElement, AvatarStackProps>(
  function AvatarStack(
    { people, size = 18, max = 3, leading, onClick, ariaLabel, className },
    ref,
  ) {
    const visible = people.slice(0, max)
    const overflow = people.length - visible.length
    const interactive = !!onClick

    const handleClick = (event: MouseEvent<HTMLSpanElement>) => {
      // The stack often sits inside a clickable card — keep
      // the stack's action (open the picker) from also
      // activating the card.
      event.stopPropagation()
      onClick?.()
    }

    const handlePointerDown = (event: PointerEvent<HTMLSpanElement>) => {
      // dnd-kit's PointerSensor listens on the card. Without
      // this, pressing the stack starts a drag and eats the
      // click.
      event.stopPropagation()
    }

    const handleKeyDown = (event: KeyboardEvent<HTMLSpanElement>) => {
      if (event.key !== "Enter" && event.key !== " ") return
      event.preventDefault()
      event.stopPropagation()
      onClick?.()
    }

    const interactiveProps = interactive
      ? {
          role: "button" as const,
          tabIndex: 0,
          onClick: handleClick,
          onPointerDown: handlePointerDown,
          onKeyDown: handleKeyDown,
          "aria-label": ariaLabel,
        }
      : {}

    return (
      <span
        ref={ref}
        data-testid="avatar-stack"
        className={`flex items-center shrink-0 ${
          interactive
            ? "cursor-pointer rounded-full focus-visible:outline-2 focus-visible:outline-[#B86A2D]"
            : ""
        } ${className ?? ""}`}
        {...interactiveProps}
      >
        {leading}
        {visible.map((person, i) => (
          <span
            key={person.id}
            title={person.name}
            className="flex items-center justify-center shrink-0 rounded-full ring-2 ring-card"
            style={{
              width: size,
              height: size,
              backgroundColor: person.color,
              marginLeft: leading || i > 0 ? -size * 0.3 : 0,
            }}
          >
            <span
              className="font-semibold text-[9px] leading-3"
              style={{ color: initialsColor(person.color) }}
            >
              {person.initials}
            </span>
          </span>
        ))}
        {overflow > 0 ? (
          <span
            className="flex items-center justify-center shrink-0 rounded-full ring-2 ring-card bg-[#E5DFD3]"
            style={{ width: size, height: size, marginLeft: -size * 0.3 }}
          >
            <span className="font-semibold text-[#5C544A] text-[9px] leading-3">
              +{overflow}
            </span>
          </span>
        ) : null}
      </span>
    )
  },
)

// Stack label: "Sarah" for one person, "Sarah +2" beyond.
// Shared by the contacts cell, the deal properties row, and
// the phone-numbers cell (which prepends its AI special
// case).
export function stackLabel(people: AvatarStackPerson[]): string {
  if (people.length === 0) return ""
  const [first, ...rest] = people
  return rest.length > 0 ? `${first.name} +${rest.length}` : first.name
}
